"use client";

import React from "react";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { trackPricingIntervalSelected } from "../analytics";
import { PLANS } from "./constants";
import { PlanCard } from "./plan-card";

const CREDIT_CONVERSIONS = [
  { action: "1 text segment (up to 160 characters)", credits: "1 credit" },
  { action: "1 picture message (MMS)", credits: "3 credits" },
  { action: "10 emails", credits: "1 credit" },
  { action: "Voice calls and voicemail", credits: "Free" },
];

export function PlanGrid() {
  const [isAnnual, setIsAnnual] = React.useState<boolean>(false);

  const handleIntervalChange = (annual: boolean) => {
    setIsAnnual(annual);
    trackPricingIntervalSelected(annual ? "year" : "month");
  };

  return (
    <>
      <div className="mx-auto mb-10 max-w-2xl text-center">
        <h1 className="text-balance text-4xl font-bold tracking-tight text-foreground sm:text-5xl md:leading-[1.1]">
          Simple, transparent pricing.
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-balance leading-normal text-muted-foreground sm:text-lg sm:leading-8">
          Every plan includes all features across text messaging, email, and voice, with unlimited
          team members and contacts.
        </p>
        <div className="mx-auto mt-5 flex items-center justify-center gap-2">
          <Label
            htmlFor="billing-interval"
            className={cn("cursor-pointer", isAnnual ? "text-muted-foreground" : "text-foreground")}
          >
            Monthly
          </Label>
          <Switch
            id="billing-interval"
            aria-label="Bill annually"
            onCheckedChange={handleIntervalChange}
            checked={isAnnual}
          />
          <Label
            htmlFor="billing-interval"
            className={cn("cursor-pointer", isAnnual ? "text-foreground" : "text-muted-foreground")}
          >
            Annual
          </Label>
          <Badge
            variant="outline"
            className="ml-1 border-primary/30 bg-primary/10 text-primary dark:border-brand-accent/30 dark:bg-brand-accent/10 dark:text-brand-accent"
          >
            Save ~20%
          </Badge>
        </div>
      </div>
      <div className="mx-auto mt-6 grid max-w-5xl gap-6 sm:grid-cols-3 md:mt-12 lg:items-center">
        {PLANS.filter((p) => (isAnnual ? p.interval === "year" : p.interval === "month")).map(
          (plan) => (
            <PlanCard key={`${plan.title}-${plan.interval}`} plan={plan} />
          ),
        )}
      </div>
      <div className="mx-auto mt-16 max-w-2xl lg:mt-20">
        <h2 className="text-base font-semibold text-foreground">What&apos;s a message credit?</h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          You send from one monthly pool of message credits across every channel. Here&apos;s how
          each type of message counts against it.
        </p>
        <table className="mt-4 w-full text-sm">
          <tbody>
            {CREDIT_CONVERSIONS.map((c) => (
              <tr key={c.action} className="border-t border-border last:border-b">
                <td className="py-2.5 pr-4 text-muted-foreground">{c.action}</td>
                <td className="whitespace-nowrap py-2.5 text-right font-medium text-foreground">
                  {c.credits}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Longer texts split into more segments, and adding an emoji or special character makes a
          text hold fewer characters per segment, so a single text can use more than one credit. See
          the FAQ below for examples.
        </p>
      </div>
    </>
  );
}
